import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Empty, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useLoad, money, fmtDate, fmtDateTime } from "@/lib/api";
import { can, DEPARTMENTS } from "@/lib/constants";

const Info = ({ l, v }) => <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</p><p className="mt-0.5 text-sm text-slate-800">{v || "—"}</p></div>;

export default function PatientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p] = useLoad(`/patients/${id}`);
  if (!p || Array.isArray(p)) return <Empty text="Loading…" />;
  return (
    <>
      <Link to="/patients" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700" data-testid="patient-back-link"><ArrowLeft className="h-4 w-4" />Patients</Link>
      <PageHeader eyebrow={p.patient_no} title={`${p.first_name} ${p.last_name}`}>
        {can(user, "createInvoice") && <Button asChild className="rounded-full" data-testid="patient-new-invoice-button"><Link to={`/invoices/new?patient=${p.id}`}><Receipt className="mr-2 h-4 w-4" />Generate invoice</Link></Button>}
      </PageHeader>
      <Panel testId="patient-info-panel">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <Info l="Gender" v={p.gender} /><Info l="Date of birth" v={p.dob} /><Info l="Phone" v={p.phone} /><Info l="Email" v={p.email} />
          <Info l="Address" v={p.address} /><Info l="Insurance" v={p.insurance_provider} /><Info l="Member no." v={p.insurance_number} /><Info l="Registered" v={fmtDate(p.created_at)} />
        </div>
      </Panel>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Consultations" testId="patient-visits-panel">
          {!p.visits.length ? <Empty text="No visits yet." /> : (
            <ul className="divide-y">{p.visits.map((v) => (
              <li key={v.id} className="py-3 text-sm">
                <div className="flex items-center justify-between"><span className="font-semibold">{DEPARTMENTS[v.department]}</span><StatusBadge status={v.status} /></div>
                <p className="text-xs text-slate-500">{fmtDateTime(v.created_at)} {v.doctor_name && `· ${v.doctor_name}`}</p>
                {v.complaint && <p className="mt-1 text-slate-600">Complaint: {v.complaint}</p>}
                {v.diagnosis && <p className="text-slate-800">Diagnosis: {v.diagnosis}</p>}
              </li>))}</ul>)}
        </Panel>
        {can(user, "finance") || can(user, "createInvoice") ? (
          <Panel title="Invoices" testId="patient-invoices-panel">
            {!p.invoices.length ? <Empty text="No invoices yet." /> : (
              <ul className="divide-y">{p.invoices.map((i) => (
                <li key={i.id}><Link to={`/invoices/${i.id}`} className="flex items-center justify-between py-3 text-sm hover:text-teal-700">
                  <span><span className="font-mono text-xs">{i.invoice_no}</span><br /><span className="text-xs text-slate-500">{fmtDate(i.created_at)}</span></span>
                  <span className="text-right"><span className="font-semibold">{money(i.total, i.currency)}</span><br /><StatusBadge status={i.status} /></span>
                </Link></li>))}</ul>)}
          </Panel>) : null}
      </div>
    </>
  );
}
